const MEGA_MENU_HOVER_EVENT = 'megaMenu:hover';
const LAZY_SELECTOR = '[data-mega-menu-lazy-products]';

/** @type {Map<string, string>} */
const contentCache = new Map();

/** @type {Map<string, boolean>} */
const emptyCache = new Map();

/** @type {Set<string>} */
const inflightKeys = new Set();

/**
 * @param {HTMLElement} element
 * @returns {string | null}
 */
function getCacheKey(element) {
  const handle = element.dataset.collectionHandle?.trim();
  const layout = element.dataset.layout || 'panel';
  const limit = element.dataset.limit || '8';

  if (!handle) return null;
  return `products:${layout}:${handle}:${limit}`;
}

/**
 * @param {HTMLElement} element
 * @returns {string | null}
 */
function buildFetchUrl(element) {
  const handle = element.dataset.collectionHandle?.trim();
  if (!handle) return null;

  const layout = element.dataset.layout || 'panel';
  const sectionId =
    layout === 'drawer'
      ? 'section-rendering-mega-menu-products-drawer'
      : 'section-rendering-mega-menu-products-panel';

  const url = new URL(`/collections/${handle}`, window.location.origin);
  url.searchParams.set('section_id', sectionId);
  return url.toString();
}

/**
 * @param {HTMLElement} element
 */
function clearPlaceholderState(element) {
  element
    .querySelectorAll('[data-mega-menu-lazy-placeholder-item], [data-mega-menu-lazy-loading]')
    .forEach((node) => node.remove());
}

/**
 * @param {HTMLElement} element
 * @param {'pending' | 'loading' | 'loaded' | 'error' | 'empty'} state
 */
function setLazyState(element, state) {
  element.classList.remove(
    'mega-menu-lazy--pending',
    'mega-menu-lazy--loading',
    'mega-menu-lazy--loaded',
    'mega-menu-lazy--error',
    'mega-menu-lazy--empty'
  );
  element.classList.add(`mega-menu-lazy--${state}`);

  const loading = element.querySelector('[data-mega-menu-lazy-loading]');
  const error = element.querySelector('[data-mega-menu-lazy-error]');
  const empty = element.querySelector('[data-mega-menu-lazy-empty]');

  if (loading instanceof HTMLElement) loading.hidden = state !== 'loading';
  if (error instanceof HTMLElement) error.hidden = state !== 'error';
  if (empty instanceof HTMLElement) empty.hidden = state !== 'empty';
}

/**
 * @param {HTMLElement} element
 * @param {string} html
 * @param {boolean} isEmpty
 */
function applyLazyContent(element, html, isEmpty) {
  if (isEmpty) {
    clearPlaceholderState(element);
    setLazyState(element, 'empty');
    element.dataset.loaded = 'true';
    return;
  }

  clearPlaceholderState(element);
  element.insertAdjacentHTML('beforeend', html);
  setLazyState(element, 'loaded');
  element.dataset.loaded = 'true';
}

/**
 * @param {HTMLElement} element
 */
async function loadLazyPanel(element) {
  if (element.dataset.loaded === 'true') return;

  const cacheKey = getCacheKey(element);
  if (!cacheKey) {
    setLazyState(element, 'error');
    element.dataset.loaded = 'true';
    return;
  }

  if (contentCache.has(cacheKey)) {
    applyLazyContent(element, contentCache.get(cacheKey) ?? '', emptyCache.get(cacheKey) === true);
    return;
  }

  if (inflightKeys.has(cacheKey)) return;

  const fetchUrl = buildFetchUrl(element);
  if (!fetchUrl) {
    setLazyState(element, 'error');
    element.dataset.loaded = 'true';
    return;
  }

  inflightKeys.add(cacheKey);
  setLazyState(element, 'loading');

  try {
    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error('Mega menu lazy load failed');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const contentNode = doc.querySelector('[data-mega-menu-lazy-content]');
    const innerHtml = contentNode?.innerHTML.trim() ?? '';
    const isEmpty = innerHtml === '';

    contentCache.set(cacheKey, innerHtml);
    emptyCache.set(cacheKey, isEmpty);

    document.querySelectorAll(LAZY_SELECTOR).forEach((panel) => {
      if (panel instanceof HTMLElement && getCacheKey(panel) === cacheKey && panel.dataset.loaded !== 'true') {
        applyLazyContent(panel, innerHtml, isEmpty);
      }
    });
  } catch {
    setLazyState(element, 'error');
    element.dataset.loaded = 'true';
  } finally {
    inflightKeys.delete(cacheKey);
  }
}

/**
 * @param {HTMLElement} drawerPanel
 * @returns {boolean}
 */
function isDrawerAccordionExpanded(drawerPanel) {
  const header = drawerPanel.previousElementSibling;
  if (header instanceof HTMLElement && header.classList.contains('active')) return true;

  if (drawerPanel.style.display === 'block') return true;

  return getComputedStyle(drawerPanel).display !== 'none';
}

/**
 * @param {HTMLElement} panel
 * @returns {boolean}
 */
function isDrawerLazyPanelVisible(panel) {
  const mainDrawer = panel.closest('.menu-drawer-container');
  if (mainDrawer instanceof HTMLElement) {
    const mainOpen = mainDrawer.classList.contains('menu-open') || mainDrawer.hasAttribute('open');
    if (!mainOpen) return false;
  }

  const nestedMenu = panel.closest('.menu-drawer__menu-container');
  if (nestedMenu instanceof HTMLElement && nestedMenu !== mainDrawer) {
    if (!nestedMenu.classList.contains('menu-open') && !nestedMenu.hasAttribute('open')) return false;
  }

  return true;
}

/**
 * @param {ParentNode} root
 */
function loadVisibleLazyPanels(root = document) {
  root.querySelectorAll(`${LAZY_SELECTOR}:not([data-loaded="true"])`).forEach((panel) => {
    if (!(panel instanceof HTMLElement)) return;

    const productsPanel = panel.closest('.mega-menu-products');
    const drawerPanel = panel.closest('.menu-drawer__menu-item-main');

    if (productsPanel) {
      if (!productsPanel.classList.contains('active')) return;
    } else if (drawerPanel) {
      if (!isDrawerAccordionExpanded(drawerPanel)) return;
      if (!isDrawerLazyPanelVisible(panel)) return;
    } else {
      return;
    }

    loadLazyPanel(panel);
  });
}

/**
 * @param {Element} listItem
 */
function activateMegaMenuTab(listItem) {
  if (listItem.classList.contains('active')) return;

  const collection = listItem.closest('.mega-menu-collection');
  if (!collection) return;

  const index = [...listItem.parentElement?.children ?? []].indexOf(listItem);
  if (index < 0) return;

  listItem.classList.add('active');
  listItem.parentElement?.querySelectorAll(':scope > li.active').forEach((item) => {
    if (item !== listItem) item.classList.remove('active');
  });

  collection.querySelectorAll('.mega-menu-products').forEach((panel, panelIndex) => {
    if (panelIndex === index) {
      panel.classList.add('active');
      const lazyPanel = panel.querySelector(`${LAZY_SELECTOR}:not([data-loaded="true"])`);
      if (lazyPanel instanceof HTMLElement) loadLazyPanel(lazyPanel);
    } else {
      panel.classList.remove('active');
    }
  });
}

function setupMegaMenuTabSwitching() {
  document.querySelectorAll('.mega-menu-collection aside li').forEach((listItem) => {
    listItem.addEventListener('mouseenter', () => {
      if (listItem instanceof HTMLElement) activateMegaMenuTab(listItem);
    });
  });
}

function setupHeaderMenuTriggers() {
  document.querySelectorAll('header-menu .menu-list__list-item').forEach((item) => {
    item.addEventListener('mouseenter', () => {
      window.requestAnimationFrame(() => loadVisibleLazyPanels(item));
    });
  });
}

/**
 * @param {ParentNode} root
 */
function scheduleDrawerLazyLoad(root) {
  window.setTimeout(() => loadVisibleLazyPanels(root), 350);
}

function setupDrawerNavigationTriggers() {
  const mainDrawer = document.querySelector('.menu-drawer-container');
  if (mainDrawer instanceof HTMLElement) {
    const mainSummary = mainDrawer.querySelector(':scope > summary');
    if (mainSummary) {
      mainSummary.addEventListener('click', () => scheduleDrawerLazyLoad(mainDrawer));
    }
    observeDrawerDetailsOpen(mainDrawer);
  }

  document.querySelectorAll('.menu-drawer__menu-container').forEach((details) => {
    if (!(details instanceof HTMLElement)) return;
    const summary = details.querySelector(':scope > summary');
    if (summary) {
      summary.addEventListener('click', () => scheduleDrawerLazyLoad(details));
    }
    observeDrawerDetailsOpen(details);
  });
}

/**
 * @param {HTMLElement} details
 */
function observeDrawerDetailsOpen(details) {
  const observer = new MutationObserver(() => {
    if (details.classList.contains('menu-open') || details.hasAttribute('open')) {
      scheduleDrawerLazyLoad(details);
    }
  });

  observer.observe(details, { attributes: true, attributeFilter: ['class', 'open'] });
}

function setupDrawerAccordionLoading() {
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const header = event.target.closest('.mega-menu-drawer--products-grid .menu-drawer__menu-item-header');
    if (!header) return;

    const content = header.nextElementSibling;
    if (!(content instanceof HTMLElement) || !content.classList.contains('menu-drawer__menu-item-main')) return;

    window.setTimeout(() => {
      if (isDrawerAccordionExpanded(content)) {
        loadVisibleLazyPanels(content);
      }
    }, 320);
  });
}

function initMegaMenuLazyLoad() {
  setupMegaMenuTabSwitching();
  setupHeaderMenuTriggers();
  setupDrawerNavigationTriggers();
  setupDrawerAccordionLoading();

  document.addEventListener(MEGA_MENU_HOVER_EVENT, () => {
    window.requestAnimationFrame(() => loadVisibleLazyPanels());
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegaMenuLazyLoad);
} else {
  initMegaMenuLazyLoad();
}
